import { Injectable } from '@angular/core';
import { BehaviorSubject, lastValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';

export type UserInfo = {
  name: string;
  avatar_url: string;
  email: string;
  user_id: string;
  access_token: string;
  app_access: boolean;
  role: string;
};

export interface UserResponse {
  user_id: string;
  email: string;
  full_name: string;
  avatar_url: string;
  app_access: boolean;
  // Backend role ('annotator' | 'admin' | ...); admin gates the developer
  // trace views. Optional so pre-#65 backends still parse.
  role?: string;
}

export interface TokenResponse {
  access_token: string;
  refresh_token?: string | null;
  token_type: string;
  user: UserResponse;
}

export type MeResponse = UserResponse;

export interface LoginRequest {
  email: string;
  password: string;
}

const ACCESS_TOKEN_KEY = 'userAccessToken';
const REFRESH_TOKEN_KEY = 'userRefreshToken';

@Injectable({
  providedIn: 'root',
})
export class OauthService {
  private userSignInSubject = new BehaviorSubject<UserInfo | null>(null);
  userSignIn$ = this.userSignInSubject.asObservable();
  user: UserInfo | null = null;

  // De-dupes concurrent restore calls (guard + menu + components on first load).
  private restorePromise: Promise<UserInfo | null> | null = null;
  private refreshPromise: Promise<UserInfo | null> | null = null;

  // Background auto-refresh timer (fires shortly before the access token expires).
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;
  // Refresh this many ms before the access token's `exp`.
  private static readonly REFRESH_SKEW_MS = 60_000;

  constructor(private http: HttpClient) {}

  /** Decode a JWT's `exp` (seconds since epoch), or null if it can't be read. */
  private getTokenExpiryMs(token: string): number | null {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return typeof payload.exp === 'number' ? payload.exp * 1000 : null;
    } catch {
      return null;
    }
  }

  /** Schedule a background refresh shortly before the access token expires. */
  private scheduleAutoRefresh(accessToken: string): void {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
    const expiryMs = this.getTokenExpiryMs(accessToken);
    // Fallback to 50 min if the token has no readable expiry.
    const fireInMs = expiryMs
      ? expiryMs - Date.now() - OauthService.REFRESH_SKEW_MS
      : 50 * 60_000;
    this.refreshTimer = setTimeout(() => {
      this.refreshSession();
    }, Math.max(fireInMs, 0));
  }

  /** A service outage is not proof that the stored credentials are invalid. */
  private isAuthRejection(error: unknown): boolean {
    return error instanceof HttpErrorResponse && [401, 403].includes(error.status);
  }

  private scheduleSessionRetry(): void {
    if (this.refreshTimer) clearTimeout(this.refreshTimer);
    // Revalidate with the server; never synthesize a successful user on failure.
    this.refreshTimer = setTimeout(() => { void this.restoreSession(); }, 30_000);
  }

  private toUserInfo(res: UserResponse, accessToken: string): UserInfo {
    return {
      name: res.full_name,
      avatar_url: res.avatar_url || '',
      email: res.email,
      user_id: res.user_id,
      access_token: accessToken,
      app_access: res.app_access,
      role: res.role ?? 'annotator',
    };
  }

  private storeTokens(res: TokenResponse): UserInfo {
    sessionStorage.setItem(ACCESS_TOKEN_KEY, res.access_token);
    if (res.refresh_token) {
      sessionStorage.setItem(REFRESH_TOKEN_KEY, res.refresh_token);
    }
    this.user = this.toUserInfo(res.user, res.access_token);
    this.userSignInSubject.next(this.user);
    this.scheduleAutoRefresh(res.access_token);
    return this.user;
  }

  private clearTokens(): void {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
    sessionStorage.removeItem(ACCESS_TOKEN_KEY);
    sessionStorage.removeItem(REFRESH_TOKEN_KEY);
    this.user = null;
    this.userSignInSubject.next(null);
  }

  /**
   * Returns the current user, restoring it from a stored token when not
   * already loaded. Pass `force` to re-fetch from the backend.
   */
  async getUser(force: boolean = false): Promise<UserInfo | null> {
    if (this.user && !force) {
      return this.user;
    }
    return this.restoreSession();
  }

  /**
   * Rehydrate the session from the stored access token via `GET /auth/me`.
   * Falls back to a refresh-token exchange if the access token has expired,
   * and clears credentials only when the server rejects authentication.
   */
  async restoreSession(): Promise<UserInfo | null> {
    const token = sessionStorage.getItem(ACCESS_TOKEN_KEY);
    if (!token) {
      this.user = null;
      return null;
    }
    if (this.restorePromise) {
      return this.restorePromise;
    }
    this.restorePromise = (async () => {
      try {
        const me = await lastValueFrom(
          this.http.get<MeResponse>(environment.backendUrl + '/auth/me'),
        );
        this.user = this.toUserInfo(me, token);
        this.userSignInSubject.next(this.user);
        this.scheduleAutoRefresh(token);
        return this.user;
      } catch (error) {
        if (this.isAuthRejection(error)) {
          return this.refreshSession();
        }
        this.scheduleSessionRetry();
        return null;
      } finally {
        this.restorePromise = null;
      }
    })();
    return this.restorePromise;
  }

  /**
   * Exchange the stored refresh token for a new access + refresh token pair.
   * Returns the rehydrated user, or null on failure. Outages retain credentials.
   */
  async refreshSession(): Promise<UserInfo | null> {
    // Single-flight: refresh tokens rotate (one use each), so when several
    // polls 401 at once only the first exchange may run — a parallel second
    // one gets "Refresh token revoked" and its clearTokens() logs the user out.
    if (this.refreshPromise) {
      return this.refreshPromise;
    }
    this.refreshPromise = this.doRefresh().finally(() => {
      this.refreshPromise = null;
    });
    return this.refreshPromise;
  }

  private async doRefresh(): Promise<UserInfo | null> {
    const refreshToken = sessionStorage.getItem(REFRESH_TOKEN_KEY);
    if (!refreshToken) {
      this.clearTokens();
      return null;
    }
    try {
      const res = await lastValueFrom(
        this.http.post<TokenResponse>(environment.backendUrl + '/auth/refresh', {
          refresh_token: refreshToken,
        }),
      );
      return this.storeTokens(res);
    } catch (error) {
      if (this.isAuthRejection(error)) {
        this.clearTokens();
      } else {
        this.scheduleSessionRetry();
      }
      return null;
    }
  }

  authState() {
    return this.userSignIn$;
  }

  /** Revoke the refresh token on the backend (best-effort), then clear state. */
  async signOut(): Promise<boolean> {
    const refreshToken = sessionStorage.getItem(REFRESH_TOKEN_KEY);
    if (refreshToken) {
      try {
        await lastValueFrom(
          this.http.post(environment.backendUrl + '/auth/logout', {
            refresh_token: refreshToken,
          }),
        );
      } catch {
        // Logout is best-effort — clear locally regardless.
      }
    }
    this.clearTokens();
    return true;
  }

  async login(email: string, password: string): Promise<void> {
    const result = await lastValueFrom(
      this.http.post<TokenResponse>(environment.backendUrl + '/auth/login', { email, password }),
    );
    this.user = this.toUserInfo(result.user, result.access_token);
    this.userSignInSubject.next(this.user);
    this.storeTokens(result);
  }

  async register(fullName: string, email: string, password: string): Promise<void> {
    const result = await lastValueFrom(
      this.http.post<TokenResponse>(environment.backendUrl + '/auth/register', {
        full_name: fullName,
        email,
        password,
      }),
    );
    this.user = this.toUserInfo(result.user, result.access_token);
    this.userSignInSubject.next(this.user);
    this.storeTokens(result);
  }
}
